// frontend/pages/VerifyEmail.js
import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import axios from 'axios';
import API_URL from '../api/baseURL';

const VerifyEmail = () => {
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("Vérification de votre compte...");
  const [isExpired, setIsExpired] = useState(false);
  const [resending, setResending] = useState(false);

  const { token: tokenParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  let token = tokenParam;
  if (!token) {
    const query = new URLSearchParams(location.search);
    token = query.get("token");
  }

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Aucun jeton de vérification trouvé.");
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/auth/verify-email/${token}`);
        setStatus("success");
        setMessage(res.data.message);
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.message || "La vérification a échoué.");
        if (err.response?.data?.expired) {
          setIsExpired(true);
        }
      }
    };
    verify();
  }, [token]);

  const handleResend = async () => {
    // Note: In a real app, you might want a prompt for email if not in state
    // But usually tokens are associated with an email we can extract or prompt for
    const email = prompt("Veuillez saisir votre adresse e-mail pour recevoir un nouveau lien :");
    if (!email) return;

    setResending(true);
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email });
      alert("Nouveau lien envoyé !");
      navigate('/login');
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'envoi.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-6">
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-10 text-center border border-gray-100 dark:border-gray-800">

        {status === "loading" && (
          <div className="space-y-6">
            <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto" />
            <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100 uppercase tracking-tighter">Vérification...</h1>
            <p className="text-gray-500">{message}</p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6 animate-in fade-in zoom-in duration-500">
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100 uppercase tracking-tighter">Succès !</h1>
            <p className="text-gray-600 dark:text-gray-400 font-medium">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-4 bg-blue-600 text-white font-black rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all transform hover:scale-[1.02]"
            >
              SE CONNECTER
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6 animate-in fade-in zoom-in duration-500">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100 uppercase tracking-tighter">Erreur</h1>
            <p className="text-red-500 font-bold bg-red-50 dark:bg-red-900/10 p-4 rounded-xl">{message}</p>

            <div className="space-y-3 pt-4">
              {isExpired && (
                <button
                  disabled={resending}
                  onClick={handleResend}
                  className="w-full py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-black rounded-xl hover:bg-gray-800 dark:hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                >
                  {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  RENVOYER UN LIEN
                </button>
              )}
              <button
                onClick={() => navigate('/signup')}
                className="w-full py-3 border-2 border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-400 font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
              >
                RETOUR À L'INSCRIPTION
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VerifyEmail;
